import React from "react";
import { ClientType } from "../../types";
import { ExportClientModal } from "./export-client-modal";
import { Button } from "../ui/button";
import { Download } from "lucide-react";

interface ExportClientButtonProps {
  client?: ClientType;
  clients?: ClientType[];
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  showIcon?: boolean;
  showText?: boolean;
  className?: string;
  onSuccess?: () => void;
  children?: React.ReactNode;
}

/**
 * Export Client Button - Wrapper component for easy integration
 * 
 * Usage:
 * ```tsx
 * // Single client export (default button)
 * <ExportClientButton client={client} />
 * 
 * // Bulk export (default button)
 * <ExportClientButton clients={selectedClients} />
 * 
 * // Custom button
 * <ExportClientButton clients={selectedClients}>
 *   <Button variant="outline">Export {count} Clients</Button>
 * </ExportClientButton>
 * 
 * // Custom styling
 * <ExportClientButton client={client} variant="default" className="bg-accent" />
 * ```
 */
export function ExportClientButton({
  client,
  clients,
  variant = "default",
  size = "default",
  showIcon = true,
  showText = true,
  className = "",
  onSuccess,
  children,
}: ExportClientButtonProps) {
  const isBulkExport = !!clients && clients.length > 0;
  const buttonText = isBulkExport 
    ? `Export ${clients.length} Client${clients.length > 1 ? 's' : ''}`
    : "Export to CSV";

  // Use custom trigger if provided via children
  const trigger = children || (
    <Button 
      variant={variant} 
      size={size}
      className={`export-client-button ${className} ${variant === "default" ? "bg-accent" : ""}`}
    >
      {showIcon && <Download className="h-4 w-4" />}
      {showText && (
        <span className={showIcon ? "ml-2" : ""}>
          {buttonText}
        </span>
      )}
    </Button>
  );

  return (
    <ExportClientModal
      client={client}
      clients={clients}
      onSuccess={onSuccess}
      trigger={trigger}
    />
  );
}

